import { HStack, VStack } from '@sgme/ui';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { ForbiddenExecutionTooltip } from '@/App/tiles/shared/ForbiddenExecutionTooltip.tsx';
import { FormattedPrice } from '@/components/ui/FormattedPrice.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';

import '@/utils/rateComponentStyling.css';
import './RatePanel.css';

type RatePanelProps = {
  type: 'spot' | 'forward';
  side: 'left' | 'right';
  way: 'Bid' | 'Ask';
  rate: number;
  highlightPosition: number;
  currencyPairPrecision: number;
  currency1: string;
  currency2: string;
  notionalCurrencyIndex: number;
  isSaleUser: boolean;
  isExecutable: boolean;
  canTrade: boolean;
  isReadonly: boolean;
  isPriceStale?: boolean;
  rateType: 'executable' | 'indicative';
  onExecute: () => void;
};

/**
 * Component displaying a single spot rate panel (bid or ask)
 */
export const RatePanel = ({
  type,
  side,
  way,
  rate,
  highlightPosition,
  currencyPairPrecision,
  currency1,
  currency2,
  notionalCurrencyIndex,
  isSaleUser,
  isExecutable,
  canTrade,
  isReadonly,
  isPriceStale = false,
  rateType,
  onExecute,
}: RatePanelProps) => {
  const { showColoredPrices } = useAppSelector(getUserPreferences);

  const alignment = 'start';
  const isBid = way === 'Bid';
  const isSell = (notionalCurrencyIndex === 1 && isBid) || (notionalCurrencyIndex === 2 && !isBid);

  const messageId = isSaleUser
    ? notionalCurrencyIndex === 1
      ? isBid
        ? 'CashTile.Rates.clientSells'
        : 'CashTile.Rates.clientBuys'
      : isBid
        ? 'CashTile.Rates.clientBuys'
        : 'CashTile.Rates.clientSells'
    : notionalCurrencyIndex === 1
      ? isBid
        ? 'CashTile.Rates.youSell'
        : 'CashTile.Rates.youBuy'
      : isBid
        ? 'CashTile.Rates.youBuy'
        : 'CashTile.Rates.youSell';

  const currency = notionalCurrencyIndex === 1 ? currency1 : currency2;

  const exponentCount = type === 'spot' ? 1 : 2;

  return (
    <div className={`w-50 h-100 ${side === 'left' ? 'border-end' : ''} text-center fw-semibold`}>
      <ForbiddenExecutionTooltip placement={side} isPriceStale={isPriceStale}>
        <VStack
          horizontalAlignment={alignment}
          verticalAlignment="full"
          fullWidth
          fullHeight
          className={clsx('px-2 py-1', 'rate-panel', {
            executable: isExecutable,
            cantTrade: isReadonly || !canTrade,
            sell: isSell,
            buy: !isSell,
            'colored-prices': showColoredPrices,
          })}
          onDoubleClick={onExecute}
        >
          <div className="fs-12 line-height-1" data-e2e={`${side}-rate-way`}>
            <FormattedMessage id={messageId} values={{ currency }} />
          </div>

          <HStack
            horizontalAlignment="center"
            verticalAlignment="end"
            fullWidth
            flex="expand"
            className="flex-fill fs-24 line-height-1 pb-2"
          >
            <FormattedPrice
              value={rate}
              exponentCount={exponentCount}
              highlightDigitPosition={highlightPosition}
              plusPrefixHidden
              precision={currencyPairPrecision}
              size="lg"
              data-e2e={`${side}-rate`}
              data-e2e-rate-type={rateType}
              data-e2e-can-trade={canTrade}
              showColoredPrices={showColoredPrices}
            />
          </HStack>
        </VStack>
      </ForbiddenExecutionTooltip>
    </div>
  );
};
